import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Star, Calendar } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EmptyState } from "@/components/common/EmptyState";
import { InlineLoading } from "@/components/common/Loading";
import { supabase } from "@/lib/supabase";
import { formatDate } from "@/lib/format";
import { addDays } from "date-fns";
import { toast } from "sonner";
import { useMatches } from "@/hooks/useMatches";
import type { FeaturedMatchRow } from "@/types/database";

function useFeatured() {
  return useQuery({
    queryKey: ["admin-featured"],
    queryFn: async (): Promise<FeaturedMatchRow[]> => {
      const { data, error } = await supabase
        .from("featured_matches")
        .select("*")
        .order("priority", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function AdminFeaturedMatches() {
  const qc = useQueryClient();
  const featured = useFeatured();
  const today = new Date().toISOString().slice(0, 10);
  const matches = useMatches({ date: today });
  const [matchId, setMatchId] = useState<number | "">("");
  const [priority, setPriority] = useState(1);
  const [expiresAt, setExpiresAt] = useState(addDays(new Date(), 2).toISOString().slice(0, 10));

  const add = useMutation({
    mutationFn: async () => {
      if (!matchId) throw new Error("Sélectionne un match");
      const { error } = await supabase.from("featured_matches").insert({ match_id: Number(matchId), priority, expires_at: expiresAt });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-featured"] });
      qc.invalidateQueries({ queryKey: ["featured-matches"] });
      setMatchId("");
      toast.success("Match ajouté");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Erreur"),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("featured_matches").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-featured"] });
      qc.invalidateQueries({ queryKey: ["featured-matches"] });
    },
  });

  return (
    <div className="space-y-4 max-w-3xl">
      <Card>
        <CardContent className="py-4 space-y-3">
          <h3 className="font-bold">Ajouter un match vedette</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="space-y-1 md:col-span-3">
              <Label>Match</Label>
              <select
                className="h-11 w-full rounded-lg border border-input bg-background px-3 text-base"
                value={matchId}
                onChange={(e) => setMatchId(Number(e.target.value) || "")}
              >
                <option value="">Choisis un match du jour…</option>
                {(matches.data ?? []).map((m) => (
                  <option key={m.id} value={m.id}>{m.home.name} vs {m.away.name} — {m.league.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Priorité (1-10)</Label>
              <Input type="number" min={1} max={10} value={priority} onChange={(e) => setPriority(Number(e.target.value))} />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label>Expire le</Label>
              <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
            </div>
          </div>
          <Button onClick={() => add.mutate()} loading={add.isPending}>
            <Plus /> Ajouter
          </Button>
        </CardContent>
      </Card>

      <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Matchs vedettes actuels</h3>
      {featured.isLoading ? (
        <InlineLoading />
      ) : (featured.data?.length ?? 0) === 0 ? (
        <EmptyState icon={Star} title="Aucun match vedette" />
      ) : (
        <div className="space-y-2">
          {featured.data!.map((f) => (
            <Card key={f.id}>
              <CardContent className="py-3 flex items-center gap-3">
                <Star className="size-5 text-gold" />
                <div className="flex-1">
                  <p className="font-semibold">Match #{f.match_id}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-2">
                    Priorité {f.priority}
                    <span className="flex items-center gap-1"><Calendar className="size-3" /> Expire {formatDate(f.expires_at)}</span>
                  </p>
                </div>
                <Button size="icon" variant="ghost" onClick={() => remove.mutate(f.id)} aria-label="delete">
                  <Trash2 className="size-4 text-destructive" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
